import { Trans, useLingui } from '@lingui/react/macro'
import cantileverThinkingGif from 'assets/cantilever-thinking.gif'
import { Skeleton } from 'designSystem/components/Skeleton'
import { Text } from 'designSystem/components/Text'
import { useMediaQuery } from 'designSystem/components/useMediaQuery'
import type { ChatTurn } from 'features/timeSeries/agentTab/chatStream'
import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'

import { TurnView } from './TurnView'

const Scroll = styled.div`
  flex: 1;
  overflow-y: auto;
  /* Reserve the scrollbar's width permanently — without this the bar pops in
     and out as content grows, re-wrapping all text each time (visible as the
     transcript "jumping" between wrapped and unwrapped). */
  scrollbar-gutter: stable;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem;
`

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 0.25rem;
`

const Loading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.25rem;
`

const SkeletonLine = styled(Skeleton)<{ $height: string; $width: string }>`
  height: ${({ $height }) => $height};
  width: ${({ $width }) => $width};
`

const Thinking = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
`

const ThinkingGif = styled.img`
  display: block;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  object-fit: contain;
`

/** The same indicator DocChat shows while it thinks, so the two chats read
 *  as one product. The gif sits out under reduced motion; the words stay. */
const ThinkingState: React.FC = () => {
  // CSS media query, not visible copy.
  // eslint-disable-next-line lingui/no-unlocalized-strings
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  return (
    <Thinking aria-live='polite'>
      {!reducedMotion && (
        <ThinkingGif
          src={cantileverThinkingGif}
          alt=''
          aria-hidden='true'
          draggable={false}
        />
      )}
      <Text variant='caption' color='tertiary'>
        <Trans>Thinking...</Trans>
      </Text>
    </Thinking>
  )
}

/** How far from the bottom still counts as "reading the latest". */
const STICK_THRESHOLD_PX = 120

const EmptyState: React.FC = () => (
  <Empty>
    <Text as='p' variant='body' color='muted' leading='relaxed'>
      <Trans>
        Describe what you want graded and the agent finds the tags, checks their
        health, computes a physics KPI, and field-fits it against a baseline
        window. Every step appears here as it happens. It can also load and edit
        any model it saved before.
      </Trans>
    </Text>
  </Empty>
)

const LoadingState: React.FC = () => {
  const { t } = useLingui()
  return (
    <Loading aria-label={t`Loading the conversation`} aria-busy='true'>
      <SkeletonLine $height='1rem' $width='60%' />
      <SkeletonLine $height='4rem' $width='100%' />
      <SkeletonLine $height='1rem' $width='40%' />
    </Loading>
  )
}

/**
 * The conversation, stuck to the bottom only while the reader is already
 * there — auto-scrolling someone who scrolled up makes the panel jump on every
 * streamed event.
 */
export const Transcript: React.FC<{
  turns: ChatTurn[]
  sending: boolean
  loading: boolean
}> = ({ turns, sending, loading }) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const stickToBottomRef = useRef(true)

  useEffect(() => {
    const element = scrollRef.current
    if (element && stickToBottomRef.current) {
      element.scrollTop = element.scrollHeight
    }
  }, [turns, sending])

  return (
    <Scroll
      ref={scrollRef}
      onScroll={(event) => {
        const element = event.currentTarget
        stickToBottomRef.current =
          element.scrollHeight - element.scrollTop - element.clientHeight <
          STICK_THRESHOLD_PX
      }}>
      {loading && <LoadingState />}
      {!loading && turns.length === 0 && <EmptyState />}
      {turns.map((turn, index) => (
        <TurnView key={index} turn={turn} />
      ))}
      {sending && <ThinkingState />}
    </Scroll>
  )
}
